import React, { useEffect, useRef } from 'react';
import useInView from '../../hooks/useInView';
import './Gallery.css';

function VideoTile({ src, poster, alt }) {
  // Preload slightly before entering viewport
  const { ref, inView } = useInView({ rootMargin: '200px 0px', threshold: 0.1 });
  const videoRef = useRef(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Lazy-attach src only when in view the first time
    if (inView && !hasLoadedRef.current) {
      if (!v.src) {
        v.src = src;
      }
      hasLoadedRef.current = true;
    }

    if (inView) {
      v.play().catch(() => {});
    } else {
      v.pause();
      v.currentTime = 0;
    }
  }, [inView]);

  return (
    <div ref={ref} className="gallery-tile">
      <video
        ref={videoRef}
        className="gallery-media"
        // defer src assignment for lazy loading
        poster={poster}
        loop
        muted
        playsInline
        preload="none"
        aria-label={alt || 'Gallery video'}
      />
    </div>
  );
}

function ImageTile({ src, alt }) {
  return (
    <div className="gallery-tile">
      <img
        src={src}
        alt={alt || 'Gallery image'}
        loading="lazy"
        className="gallery-media"
      />
    </div>
  );
}

function PdfTile({ src, alt }) {
  return (
    <div className="gallery-tile">
      <object
        data={src}
        type="application/pdf"
        className="gallery-media pdf-embed"
        aria-label={alt || 'PDF document'}
      >
        <a href={src} target="_blank" rel="noopener noreferrer">Open PDF menu</a>
      </object>
    </div>
  );
}

export default function Gallery({ items }) {
  return (
    <div className="gallery-grid">
      {items.map((item, idx) =>
        item.type === 'video' ? (
          <VideoTile key={idx} src={item.src} poster={item.poster} alt={item.alt} />
        ) : item.type === 'pdf' ? (
          <PdfTile key={idx} src={item.src} alt={item.alt} />
        ) : (
          <ImageTile key={idx} src={item.src} alt={item.alt} />
        )
      )}
    </div>
  );
}
